import { FC, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useFeatureParam, useIsAuthenticated } from '../../scripts/selectors/global';
import { AdblockModal } from './AdblockModal';
import { useAdblockDetection } from './useAdblockDetection';
import {
  ADBLOCK_MODAL_ROLLOUT_PARAM,
  ADBLOCK_MODAL_ROLLOUT_DEFAULT,
  ADBLOCK_MODAL_MUTE_HOURS,
  ADBLOCK_MODAL_DISPLAY_DELAY_MS,
  ADBLOCK_MODAL_DISPLAY_DELAY_MOBILE_MS
} from '../../scripts/adblockConstants';
import { useIsMobile } from '../../scripts/hooks/useIsMobile';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

// Frequency capping configuration
const ADBLOCK_MODAL_STORAGE_KEY = 'rio:adblock-modal-dismissed';
const MUTE_MS = ADBLOCK_MODAL_MUTE_HOURS * 60 * 60 * 1000;

// Debug mode: add ?debug_adblock_modal=1 to URL to force show on every load
const DEBUG_QUERY_PARAM = 'debug_adblock_modal';

/**
 * Check if the modal should be shown based on frequency capping.
 */
function shouldShowModal(): boolean {
  if (typeof window === 'undefined' || !window.localStorage) {
    return true;
  }

  try {
    const stored = window.localStorage.getItem(ADBLOCK_MODAL_STORAGE_KEY);
    if (!stored) {
      return true;
    }

    const dismissedAt: number = JSON.parse(stored);
    return Date.now() - dismissedAt >= MUTE_MS;
  } catch {
    return true;
  }
}

/**
 * Record that the modal was dismissed.
 */
function recordDismissal(): void {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  try {
    window.localStorage.setItem(ADBLOCK_MODAL_STORAGE_KEY, String(Date.now()));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Track analytics event.
 */
function trackEvent(action: string, contentId?: string): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }

  window.gtag('event', action, {
    event_category: 'adblock',
    content_id: contentId
  });
}

// Session storage key for rollout decision
const ROLLOUT_SESSION_KEY = 'rio:adblock-modal-rollout';

/**
 * Check if user is in the rollout group for this session.
 * Decision is cached in sessionStorage for consistency within a session.
 */
function isInRolloutGroup(rolloutPercent: number): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  if (rolloutPercent <= 0) {
    return false;
  }
  if (rolloutPercent >= 100) {
    return true;
  }

  try {
    const cached = sessionStorage.getItem(ROLLOUT_SESSION_KEY);
    if (cached !== null) {
      return cached === 'true';
    }

    const inGroup = Math.random() * 100 < rolloutPercent;
    sessionStorage.setItem(ROLLOUT_SESSION_KEY, String(inGroup));
    return inGroup;
  } catch {
    return false;
  }
}

export const AdblockModalContainer: FC = () => {
  const { isBlocked, isReady } = useAdblockDetection();
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isVisible, setIsVisible] = useState(false);
  const [hasTrackedImpression, setHasTrackedImpression] = useState(false);
  const debugMode = new URLSearchParams(location.search).get(DEBUG_QUERY_PARAM) === '1';

  // Get rollout percentage from backend feature params (0-100)
  const rolloutPercent = useFeatureParam(ADBLOCK_MODAL_ROLLOUT_PARAM, (v) => {
    const n = parseInt(v || '', 10);
    return isNaN(n) ? ADBLOCK_MODAL_ROLLOUT_DEFAULT : Math.max(0, Math.min(100, n));
  });

  const displayDelay = isMobile ? ADBLOCK_MODAL_DISPLAY_DELAY_MOBILE_MS : ADBLOCK_MODAL_DISPLAY_DELAY_MS;

  // Determine if modal should show (with delay so users engage with content first)
  useEffect(() => {
    // Debug mode: show immediately, skip detection, frequency, and delay checks
    if (debugMode) {
      setIsVisible(true);
      return;
    }

    if (isReady && isBlocked && shouldShowModal()) {
      if (isInRolloutGroup(rolloutPercent)) {
        const timer = setTimeout(() => setIsVisible(true), displayDelay);
        return () => clearTimeout(timer);
      } else {
        trackEvent('adblock_modal_suppressed', 'rollout');
      }
    }
  }, [isReady, isBlocked, debugMode, rolloutPercent, displayDelay]);

  // Track impression when modal becomes visible
  useEffect(() => {
    if (isVisible && !hasTrackedImpression) {
      trackEvent('adblock_modal_shown');
      setHasTrackedImpression(true);
    }
  }, [isVisible, hasTrackedImpression]);

  const dismissModal = useCallback((eventAction: string, contentId?: string) => {
    trackEvent(eventAction, contentId);
    if (!debugMode) {
      recordDismissal();
    }
    setIsVisible(false);
  }, [debugMode]);

  const handleDismiss = useCallback((method: 'continue_without' | 'overlay_click' | 'escape_key') => {
    dismissModal('adblock_modal_ignored', method);
  }, [dismissModal]);

  const handleCtaClick = useCallback((action: 'unblock' | 'premium' | 'login') => {
    dismissModal(`adblock_modal_${action}`);
  }, [dismissModal]);

  const loginUrl = `/login?next=${encodeURIComponent(location.pathname + location.search)}`;

  if (!isVisible) {
    return null;
  }

  return (
    <AdblockModal
      onDismiss={handleDismiss}
      onCtaClick={handleCtaClick}
      unblockUrl="/news/342-how-to-unblock-ads-on-raiderio"
      premiumUrl="/premium"
      loginUrl={loginUrl}
      showLoginLink={!isAuthenticated}
    />
  );
};
